import type { CrawlUrlNode, WithResourceId } from '../../types/knowledge';

export const SYNTHETIC_CRAWL_NODE_ID_PREFIX = 'synthetic:';

export type CrawlUrlTreeNode = WithResourceId<CrawlUrlNode> & {
  children: CrawlUrlTreeNode[];
  isSynthetic: boolean;
};

type NormalizedCrawlUrlNode = CrawlUrlTreeNode & {
  pathKey: string;
  origin: string;
  segments: string[];
};

export function isSyntheticCrawlNodeId(id: string): boolean {
  return id.startsWith(SYNTHETIC_CRAWL_NODE_ID_PREFIX);
}

/** Keeps only backend-mapped node ids — drops UI-only synthetic parents. */
export function filterRealCrawlNodeIds(ids: string[]): string[] {
  return ids.filter((id) => !isSyntheticCrawlNodeId(id));
}

function normalizeUrlPath(url: string): {
  pathKey: string;
  origin: string;
  segments: string[];
} {
  try {
    const parsed = new URL(url);
    const segments = parsed.pathname.split('/').filter(Boolean);
    const pathname = segments.length === 0 ? '' : `/${segments.join('/')}`;
    return {
      origin: parsed.origin,
      segments,
      pathKey: `${parsed.origin}${pathname}`,
    };
  } catch {
    const trimmed = url.replace(/\/+$/, '');
    return {
      origin: '',
      segments: trimmed ? [trimmed] : [],
      pathKey: trimmed,
    };
  }
}

function ancestorPathKey(
  origin: string,
  segments: string[],
  segmentCount: number,
): string {
  if (segmentCount <= 0) {
    return origin;
  }

  return `${origin}/${segments.slice(0, segmentCount).join('/')}`;
}

function urlForPathKey(
  pathKey: string,
  origin: string,
  segmentCount: number,
): string {
  if (segmentCount <= 0) {
    return `${origin}/`;
  }

  return `${pathKey}/`;
}

function createSyntheticNode(
  origin: string,
  segments: string[],
): NormalizedCrawlUrlNode {
  const pathKey = ancestorPathKey(origin, segments, segments.length);
  return {
    id: `${SYNTHETIC_CRAWL_NODE_ID_PREFIX}${pathKey}`,
    url: urlForPathKey(pathKey, origin, segments.length),
    parentId: undefined,
    pathSegment: segments.at(-1) ?? null,
    selected: false,
    depth: segments.length,
    children: [],
    isSynthetic: true,
    pathKey,
    origin,
    segments,
  };
}

/**
 * Builds a URL-path tree and inserts synthetic parents for missing path
 * segments (e.g. `/test/` when only `/test/a` and `/test/b` were mapped).
 */
export function buildCrawlUrlTree(
  nodes: Array<WithResourceId<CrawlUrlNode>>,
): CrawlUrlTreeNode[] {
  const items: NormalizedCrawlUrlNode[] = nodes.map((node) => ({
    ...node,
    children: [],
    isSynthetic: false,
    ...normalizeUrlPath(node.url ?? ''),
  }));

  const byPathKey = new Map<string, NormalizedCrawlUrlNode>();
  for (const item of items) {
    if (!byPathKey.has(item.pathKey)) {
      byPathKey.set(item.pathKey, item);
    }
  }

  for (const item of [...items]) {
    for (let depth = 0; depth < item.segments.length; depth += 1) {
      const key = ancestorPathKey(item.origin, item.segments, depth);
      if (byPathKey.has(key)) {
        continue;
      }

      const synthetic = createSyntheticNode(
        item.origin,
        item.segments.slice(0, depth),
      );
      byPathKey.set(key, synthetic);
      items.push(synthetic);
    }
  }

  items.sort(
    (left, right) =>
      left.segments.length - right.segments.length ||
      left.pathKey.localeCompare(right.pathKey) ||
      left.id.localeCompare(right.id),
  );

  const roots: CrawlUrlTreeNode[] = [];
  const attached = new Set<string>();

  for (const item of items) {
    if (attached.has(item.id)) {
      continue;
    }

    let parent: NormalizedCrawlUrlNode | undefined;

    for (let depth = item.segments.length - 1; depth >= 0; depth -= 1) {
      const key = ancestorPathKey(item.origin, item.segments, depth);
      const candidate = byPathKey.get(key);
      if (candidate && candidate.id !== item.id) {
        parent = candidate;
        break;
      }
    }

    if (parent) {
      parent.children.push(item);
      attached.add(item.id);
    } else {
      roots.push(item);
      attached.add(item.id);
    }
  }

  const sortChildren = (node: CrawlUrlTreeNode) => {
    node.children.sort(
      (left, right) =>
        (left.url ?? '').localeCompare(right.url ?? '') ||
        left.id.localeCompare(right.id),
    );
    for (const child of node.children) {
      sortChildren(child);
    }
  };

  for (const root of roots) {
    sortChildren(root);
  }

  roots.sort(
    (left, right) =>
      (left.url ?? '').localeCompare(right.url ?? '') ||
      left.id.localeCompare(right.id),
  );

  return roots;
}
